import {
  Text,
  Title,
  Stack,
  SegmentedControl,
  Box,
  Button,
  Center,
  TextInput,
  Skeleton,
  SimpleGrid,
  Flex,
  Loader,
} from "@mantine/core";
import { Masonry } from "masonic";
import { scriptsQueryOptions } from "@/api/fapinstructor/scripts/get-scripts";
import { ScriptCard } from "@/components/script/script-card";
import { useNavigate } from "@tanstack/react-router";
import type { Script } from "@fapinstructor/common";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Page } from "@/components/page";
import { useInfiniteQuery } from "@tanstack/react-query";
import { TbSearch } from "react-icons/tb";
import { useDebouncedState } from "@mantine/hooks";
import { DelayedComponent } from "@/components/delayed-component";

export const ExploreScriptsViewSchema = z.enum(["all", "bookmarks"]);

const ExploreScriptsSearchSchema = z.object({
  view: ExploreScriptsViewSchema.optional().default("all"),
  search: z.string().optional(),
});

export type ExploreScriptsSearch = z.infer<typeof ExploreScriptsSearchSchema>;

export const Route = createFileRoute(
  "/_authenticated/_subscribed/_main/explore-scripts",
)({
  loaderDeps: ({ search: { view, search } }) => ({ view, search }),
  loader: (opts) =>
    opts.context.queryClient.ensureInfiniteQueryData(
      scriptsQueryOptions({
        bookmarked: opts.deps.view === "bookmarks",
        search: opts.deps.search,
      }),
    ),
  validateSearch: zodValidator(ExploreScriptsSearchSchema),
  component: ExploreScriptsRouteComponent,
});

function ExploreScriptsRouteComponent() {
  const { view, search: initialSearch } = Route.useSearch();
  const [search, setSearch] = useDebouncedState(initialSearch, 300);
  const scriptsQuery = useInfiniteQuery(
    scriptsQueryOptions({
      bookmarked: view === "bookmarks",
      search,
    }),
  );
  const scripts = scriptsQuery.data?.pages.flatMap((p) => p.data) ?? [];
  const navigate = useNavigate({ from: "/explore-scripts" });

  return (
    <Page title="Scripts">
      <Title>Scripts</Title>
      <Text mb="md">Explore scripts made by the community.</Text>
      <Stack>
        <TextInput
          placeholder="Search for scripts..."
          defaultValue={search}
          onChange={(event) => {
            const newSearch = event.target.value;
            setSearch(newSearch);
          }}
          leftSection={<TbSearch />}
        />
        <Box>
          <SegmentedControl
            data={[
              { label: "All", value: "all" },
              { label: "Bookmarks", value: "bookmarks" },
            ]}
            defaultValue={view}
            onChange={(view) => {
              // TODO: There must be a better way instead of parsing.
              const parsedView = ExploreScriptsViewSchema.parse(view);
              // searchParams.set("view", view);
              navigate({
                search: (prev) => ({
                  ...prev,
                  view: parsedView,
                }),
              });
            }}
          />
        </Box>
        {scripts.length > 0 ? (
          <Masonry
            key={crypto.randomUUID()}
            items={scripts}
            columnGutter={16}
            columnWidth={300}
            render={MasonryCard}
          />
        ) : null}
        {scriptsQuery.isLoading && <ScriptLoader />}
        {scripts.length === 0 && !scriptsQuery.isLoading ? (
          <NoResultsFound search={search ?? ""} />
        ) : null}
        {scriptsQuery.hasNextPage ? (
          <Center>
            <Button
              loading={scriptsQuery.isFetchingNextPage}
              onClick={() => {
                scriptsQuery.fetchNextPage();
              }}
            >
              Load More
            </Button>
          </Center>
        ) : null}
      </Stack>
    </Page>
  );
}

function ScriptLoader() {
  return (
    <DelayedComponent delay={1000}>
      <Center p="xl">
        <Loader color="pink" type="dots" />
      </Center>
    </DelayedComponent>
  );
}

type NoResultsFoundProps = {
  search: string;
};

function NoResultsFound({ search }: NoResultsFoundProps) {
  return <Text>Sorry, no scripts were found matching "{search}"</Text>;
}

type MasonryCardProps = { data: Script };

function MasonryCard({ data: script }: MasonryCardProps) {
  return <ScriptCard key={script.id} script={script} />;
}
